import { useRef } from 'react'
import { X, Barcode } from 'lucide-react'
import { useUIStore } from '@/stores/uiStore'
import { ProductCard } from '@/components/products/ProductCard'
import type { Product } from '@/types'

export function BarcodeMatchModal() {
  const { closeModal, modalData } = useUIStore()
  // Latch against double-taps. ProductCard wires both its `onClick`
  // (card body) and `onAddToCart` (+ button) to handlePick, and React
  // batches the closeModal state update until the current handler
  // returns -- a fast double-tap can fire two clicks before the modal
  // unmounts, which would otherwise add the product to the cart twice.
  const pickedRef = useRef(false)

  const candidates = (modalData?.candidates as Product[] | undefined) ?? []
  const scannedCode = modalData?.scannedCode as string | undefined
  const onPick = modalData?.onPick as ((product: Product) => void) | undefined

  if (candidates.length === 0) return null

  const handlePick = (product: Product) => {
    if (pickedRef.current) return
    pickedRef.current = true
    onPick?.(product)
    closeModal()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeModal}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 overflow-hidden animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Barcode size={20} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Multiple products match
              </h2>
              <p className="text-sm text-gray-500">
                {scannedCode
                  ? `Code "${scannedCode}" matches ${candidates.length} in-stock products. Tap the one to add.`
                  : `${candidates.length} in-stock products share this code. Tap the one to add.`}
              </p>
            </div>
          </div>
          <button
            onClick={closeModal}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {candidates.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handlePick}
                onClick={handlePick}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
